/**
 * AfterCare — EMR (Electronic Medical Record) data store.
 *
 * localStorage-backed, fully offline. Follows the same lsGet/lsSet
 * pattern as db.ts. Seeds demo EMR data for Manisha Patel on first run.
 *
 * Zero network calls — everything runs on-device.
 */

import type { EmrRecord, EmrAllergy, EmrLabResult, EmrDiagnosis, EmrFollowUp, EmrVitalSign } from '../types/emr';
import { MANISHA_EMR } from '../data/emr-demo-data';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LS_KEY_EMR = 'aftercare:emr_record';

// ---------------------------------------------------------------------------
// Helpers (same pattern as db.ts)
// ---------------------------------------------------------------------------

function lsGet<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return fallback;
}

function lsSet(key: string, value: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(value)); }
  catch (err) { console.warn('[AfterCare] localStorage write failed:', err); }
}

// ---------------------------------------------------------------------------
// Public API — EMR record CRUD
// ---------------------------------------------------------------------------

export function getEmrRecord(): EmrRecord | null {
  return lsGet<EmrRecord | null>(LS_KEY_EMR, null);
}

export function seedDemoEmr(): void {
  if (getEmrRecord()) return;
  console.log('[AfterCare] Seeding demo EMR for Manisha Patel...');
  lsSet(LS_KEY_EMR, MANISHA_EMR);
  console.log('[AfterCare] Demo EMR seeded');
}

// ---------------------------------------------------------------------------
// Public API — Filtered queries
// ---------------------------------------------------------------------------

export function getActiveAllergies(): EmrAllergy[] {
  const emr = getEmrRecord();
  if (!emr) return [];
  return emr.allergies.filter(a => a.status === 'active');
}

export function getActiveDiagnoses(): EmrDiagnosis[] {
  const emr = getEmrRecord();
  if (!emr) return [];
  return emr.diagnoses.filter(d => d.status === 'active');
}

export function getAbnormalLabs(): EmrLabResult[] {
  const emr = getEmrRecord();
  if (!emr) return [];
  return emr.labResults.filter(l => l.interpretation !== 'normal');
}

export function getLatestVitals(): EmrVitalSign | null {
  const emr = getEmrRecord();
  if (!emr || emr.vitalSigns.length === 0) return null;
  return emr.vitalSigns.sort((a, b) => b.date.localeCompare(a.date))[0];
}

export function getUpcomingFollowUps(): EmrFollowUp[] {
  const emr = getEmrRecord();
  if (!emr) return [];
  return emr.dischargeSummary.followUpAppointments.sort((a, b) => a.date.localeCompare(b.date));
}
